import { useCallback, useRef, useState } from "react";
import { useBoolean } from "usehooks-ts";

import { MonitoringEventTypeType } from "../../../shared/types/monitoring";
import { EditModeButton } from "../EditModeButton";

import { TimerControls } from "./TimerControls";

import { serverClearVotes } from "api/api-server-actions";
import { BreakoutGroupsConfigForm } from "components/footer/tools-menu/BreakoutGroupConfigForm";
import { ProjectingButton } from "components/materials/presentation/ProjectingButton";
import { openProjectorView } from "components/materials/presentation/projector/projector-messaging";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import { StartRecordingMenuItem, StopRecordingMenuItem } from "components/menus/RecordingMenuItems";
import { RecordingSessionState } from "components/server-types";
import { t } from "i18n/i18n";
import { Dialog } from "mds/components/Dialog";
import { IconText } from "mds/components/IconText";
import { Menu, MenuDivider, type MenuInstance, MenuItem } from "mds/components/Menu";
import { ToggleSwitch } from "mds/components/ToggleSwitch";
import { Tooltip } from "mds/components/Tooltip";
import { RoundedButton } from "mds/components/buttons/RoundedButton";
import { useIsLgOrLarger } from "mds/hooks/use-responsive";
import {
  ChevronUpIcon,
  ClockIcon,
  MicrophoneOffIcon,
  PersonGroupIcon,
  ReactionClearIcon,
  ToolsIcon,
} from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import {
  selectActiveClassSession,
  selectActiveRecordingSessionForCurrentSection,
  selectClassTimerEndsAt,
  selectCurrentPageId,
  selectCurrentSection,
  selectCurrentUserId,
  selectIsClassSessionVirtual,
} from "store/selectors";
import { selectCanStudentsScreenshare } from "store/selectors/virtual-classes";
import { useAvPubsubMessaging } from "utils/av";
import { useCreateMonitoringEvent } from "utils/hooks/use-create-monitoring-event";
import { startProjecting, usePresentation } from "utils/presentation";
import { isLinkedSection } from "utils/publishing";

const ToolsMenu = () => {
  const isVirtualClassSession = useAppSelector(selectIsClassSessionVirtual);
  const { sendPubsubVirtualClassStateMessage } = useAvPubsubMessaging();
  const currentUserId = useAppSelector(selectCurrentUserId);
  const canStudentsScreenshare = useAppSelector(selectCanStudentsScreenshare);
  const createMonitoringEvent = useCreateMonitoringEvent();

  const activeClassSession = useAppSelector(selectActiveClassSession);
  const isBreakoutsInProgress = !!activeClassSession?.breakout_session_ends_at;

  const activeRecordingSession = useAppSelector(selectActiveRecordingSessionForCurrentSection);
  const isRecordingSessionStarting =
    activeRecordingSession?.state === RecordingSessionState.STARTING;
  const isRecordingSessionStarted = activeRecordingSession?.state === RecordingSessionState.STARTED;

  const endTime = useAppSelector(selectClassTimerEndsAt);
  const [showTimerControls, setShowTimerControls] = useState(false);
  const menuRef = useRef<MenuInstance>(null);

  const selectedPageId = useAppSelector(selectCurrentPageId);
  const { isPresenting, unfeaturedTab, sectionId, presentedSessionId } = usePresentation();

  const currentSection = useAppSelector(selectCurrentSection);
  const isLinked = isLinkedSection(currentSection);
  const isLargeScreen = useIsLgOrLarger();
  const isAssessing = useIsAssessing();

  const onProjecting = () => {
    if (!sectionId || !presentedSessionId || !selectedPageId) {
      return Promise.resolve();
    }
    openProjectorView(sectionId);
    return startProjecting(presentedSessionId, {
      // We add these fields to ensure the server validation passes,
      // just in case the projecting action is taken before any presenting action.
      presented_page_id: selectedPageId,
      presenting_user_id: currentUserId,
    });
  };

  const onTimerClose = useCallback(() => {
    setShowTimerControls(false);
    menuRef.current?.closeMenu();
  }, []);

  const onMuteAll = useCallback(() => {
    sendPubsubVirtualClassStateMessage({
      action: "mute-all",
    });
    createMonitoringEvent(MonitoringEventTypeType.FORCE_MUTE_ALL, {});
  }, [createMonitoringEvent, sendPubsubVirtualClassStateMessage]);

  const onClearAllReactions = useCallback(() => {
    if (activeClassSession) {
      serverClearVotes(activeClassSession.id);
    }
  }, [activeClassSession]);

  const onToggleStudentScreenshare = () => {
    if (activeClassSession) {
      storeApi.class_sessions.partial_update({
        id: activeClassSession.id,
        can_students_screenshare: !canStudentsScreenshare,
      });
    }
  };

  const {
    value: breakoutsDialogIsOpen,
    setTrue: openBreakoutsDialog,
    setFalse: closeBreakoutsDialog,
  } = useBoolean(false);

  const MenuButton = (
    <RoundedButton eventName="Classroom Tools menu opened" kind="tertiary">
      <IconText
        iconEnd={<ChevronUpIcon />}
        iconStart={<ToolsIcon />}
        text={t("footer.tool.title")}
      />
    </RoundedButton>
  );

  if (!activeClassSession) {
    return null;
  }

  return (
    <>
      <Menu
        className="dark-menu min-w-[180px]"
        direction="top"
        instanceRef={menuRef}
        menuButton={MenuButton}
        onMenuChange={(e) => {
          if (!e.open) setShowTimerControls(false);
        }}
      >
        {!isLargeScreen && !isLinked && (
          <>
            <EditModeButton isDisabled={isAssessing} isMenuItem />
            <MenuDivider />
          </>
        )}

        {showTimerControls ? (
          <TimerControls onClose={onTimerClose} />
        ) : (
          <>
            {isVirtualClassSession && (
              <MenuItem
                disabled={isBreakoutsInProgress}
                eventName="Create Breakout Groups"
                onClick={openBreakoutsDialog}
              >
                <Tooltip
                  text={
                    isBreakoutsInProgress ? t("breakouts_banner.breakouts_in_progress") : undefined
                  }
                >
                  <IconText
                    iconStart={<PersonGroupIcon />}
                    text={t("footer.tool.create_breakout_groups")}
                  />
                </Tooltip>
              </MenuItem>
            )}

            <MenuItem
              disabled={!!endTime || isBreakoutsInProgress}
              eventName="Classroom Tools - Set Timer"
              onClick={(e) => {
                // eslint-disable-next-line no-param-reassign
                e.keepOpen = true;
                setShowTimerControls(true);
              }}
            >
              <IconText iconStart={<ClockIcon />} text={t("footer.tool.timer_set")} />
            </MenuItem>

            <MenuDivider />

            {isVirtualClassSession && (
              <MenuItem eventName="Classroom Tools - Mute all" onClick={onMuteAll}>
                <IconText iconStart={<MicrophoneOffIcon />} text={t("footer.tool.mute_all")} />
              </MenuItem>
            )}

            <MenuItem
              eventName="Classroom Tools - Clear all reactions"
              onClick={onClearAllReactions}
            >
              <IconText
                iconStart={<ReactionClearIcon />}
                text={t("footer.tool.clear_all_reactions")}
              />
            </MenuItem>

            {isVirtualClassSession && (
              <>
                <MenuDivider />

                {isRecordingSessionStarting || isRecordingSessionStarted ? (
                  <StopRecordingMenuItem recordingSessionId={activeRecordingSession.id} />
                ) : (
                  <StartRecordingMenuItem sectionId={activeClassSession.section_id} />
                )}

                <MenuDivider />

                <MenuItem
                  eventName="Toggle Student Screenshare"
                  onClick={onToggleStudentScreenshare}
                >
                  <div className="flex w-full items-center justify-between">
                    <span className="mr-2">{t("av.screenshare.student_screenshare_toggle")}</span>
                    <ToggleSwitch
                      isSelected={canStudentsScreenshare}
                      onClick={onToggleStudentScreenshare}
                    />
                  </div>
                </MenuItem>
              </>
            )}

            {isPresenting && !unfeaturedTab && !isLargeScreen && (
              <>
                <MenuDivider />
                <ProjectingButton isMenuItem onProject={onProjecting} />
              </>
            )}
          </>
        )}
      </Menu>

      <Dialog
        open={breakoutsDialogIsOpen}
        title={t("breakout_group_modal.title")}
        onClose={closeBreakoutsDialog}
      >
        <BreakoutGroupsConfigForm onClose={closeBreakoutsDialog} />
      </Dialog>
    </>
  );
};

// Using default export to allow lazy-loading
// eslint-disable-next-line import/no-default-export
export default ToolsMenu;
